import { existsSync, readFileSync, writeFileSync } from 'fs';
import { join } from 'path';
import { parseYaml, stringifyYaml } from './yaml.js';

/**
 * `.rekor.yaml` — a committed, repo-root file that binds this repository to one organization
 * (mirrors wayai's `.wayai.yaml`, and sits alongside it so both CLIs coexist). Written by
 * `rekor init`; read by the active-org resolver, which forwards `org_id` as the
 * `X-Rekor-Active-Org` header so multi-org humans operate against the chosen org rather than
 * their backend primary. No secrets — safe to commit.
 */
export const ROOT_CONFIG_FILE = '.rekor.yaml';

export interface RootConfig {
  org_id: string;
  /** Human-readable org name — display only, not authoritative. */
  org_name?: string;
}

function rootConfigPath(gitRoot: string): string {
  return join(gitRoot, ROOT_CONFIG_FILE);
}

/** Read `.rekor.yaml` from the git root. Returns null when absent, empty, or malformed. */
export function readRootConfig(gitRoot: string): RootConfig | null {
  const path = rootConfigPath(gitRoot);
  if (!existsSync(path)) return null;
  try {
    const parsed = parseYaml(readFileSync(path, 'utf-8')) as Partial<RootConfig> | null;
    if (!parsed || typeof parsed.org_id !== 'string' || parsed.org_id.length === 0) return null;
    const out: RootConfig = { org_id: parsed.org_id };
    if (typeof parsed.org_name === 'string') out.org_name = parsed.org_name;
    return out;
  } catch {
    return null;
  }
}

/** Write `.rekor.yaml` at the git root. Returns the path written. */
export function writeRootConfig(gitRoot: string, config: RootConfig): string {
  const path = rootConfigPath(gitRoot);
  writeFileSync(path, stringifyYaml(config));
  return path;
}
